import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  EmptyState,
  PageHeader,
  ScoreBar,
  SeverityBadge,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { DeltaChip } from '../components/DeltaChip';
import {
  getOrgControlHealth,
  getOrgDashboard,
  getOrgJurisdictions,
} from '../lib/api';

/**
 * /o/:orgSlug/dashboard — Org-tier aggregate dashboard.
 *
 * NAV-4-2: Promoted aggregate readiness to hero tile (span 2 columns),
 * replaced workspace count card with per-workspace readiness bars,
 * added Open Findings card and Incidents card with clickable affordances,
 * added FreshnessStamp-style as-of date.
 */

export function OrgDashboard() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();

  const dashboardQuery = useQuery({
    queryKey: queryKeys.orgDashboard(orgSlug ?? ''),
    queryFn: () => getOrgDashboard(orgSlug!),
    enabled: !!orgSlug,
  });
  const dashboard = {
    data: dashboardQuery.data ?? null,
    error: dashboardQuery.error?.message ?? null,
    loading: dashboardQuery.isLoading,
  };
  const controlHealthQuery = useQuery({
    queryKey: queryKeys.orgControlHealth(orgSlug ?? ''),
    queryFn: () => getOrgControlHealth(orgSlug!),
    enabled: !!orgSlug,
  });
  const controlHealth = {
    data: controlHealthQuery.data ?? null,
    error: controlHealthQuery.error?.message ?? null,
    loading: controlHealthQuery.isLoading,
  };
  const jurisdictionsQuery = useQuery({
    queryKey: queryKeys.orgJurisdictions(orgSlug ?? ''),
    queryFn: () => getOrgJurisdictions(orgSlug!),
    enabled: !!orgSlug,
  });
  const jurisdictions = {
    data: jurisdictionsQuery.data ?? null,
    error: jurisdictionsQuery.error?.message ?? null,
    loading: jurisdictionsQuery.isLoading,
  };

  if (dashboard.loading) return <Spinner label="Loading organization dashboard" />;
  if (dashboard.error) return <Alert variant="error">{dashboard.error}</Alert>;
  if (!dashboard.data) return null;

  const d = dashboard.data;

  // Derive finding / incident counts from top_blockers (best available
  // until dedicated org-level finding/incident endpoints are wired).
  const openFindingsCount = d.top_blockers.length;
  const openIncidentsCount = d.top_blockers.filter(
    (b) => b.severity === 'critical' || b.severity === 'high',
  ).length;

  // V3-8-7: week-over-week deltas for KPI cards.
  const scoreDelta =
    d.previous_aggregate_score != null
      ? d.aggregate_score - d.previous_aggregate_score
      : null;
  const findingsDelta =
    d.previous_findings_count != null
      ? openFindingsCount - d.previous_findings_count
      : null;
  const incidentsDelta =
    d.previous_incidents_count != null
      ? openIncidentsCount - d.previous_incidents_count
      : null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title={d.organization.display_name}
        subtitle={`${d.workspace_count} workspace${d.workspace_count === 1 ? '' : 's'} · ${d.organization.plan} plan`}
      />

      {/* As-of freshness line */}
      <AsOfStamp />

      {/* KPI grid — hero readiness (span 2) + findings + incidents */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'var(--space-3)',
        }}
      >
        {/* Aggregate Readiness — hero tile, spans 2 columns */}
        <div
          style={{
            gridColumn: 'span 2',
            border: '2px solid var(--accent)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
            }}
          >
            Aggregate Readiness
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
            }}
          >
            <span
              style={{
                fontSize: 48,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                color: scoreColor(d.aggregate_score),
                lineHeight: 1,
              }}
            >
              {d.aggregate_score}%
            </span>
            <DeltaChip
              value={scoreDelta}
              sentiment="positive"
              label="vs last week"
            />
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
            }}
          >
            across {d.workspace_count} workspace{d.workspace_count === 1 ? '' : 's'}
          </div>
        </div>

        {/* Open Findings card — clickable */}
        <button
          type="button"
          onClick={() => orgSlug && navigate(`/o/${orgSlug}/findings`)}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-4)',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-1)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 500,
            }}
          >
            Open Findings
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 'var(--space-1)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                color: openFindingsCount > 0 ? 'var(--status-red)' : 'var(--text)',
              }}
            >
              {openFindingsCount}
            </span>
            <DeltaChip
              value={findingsDelta}
              sentiment="negative"
              label="vs last week"
            />
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              marginTop: 'var(--space-1)',
            }}
          >
            Across {d.workspace_count} workspace{d.workspace_count === 1 ? '' : 's'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              style={{
                fontSize: 'var(--font-size-3xs)',
                color: 'var(--text-muted)',
              }}
            >
              View all
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
              style={{ color: 'var(--text-muted)' }}
            >
              <path
                d="M4.5 2.5l3 3.5-3 3.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>

        {/* Incidents card — clickable */}
        <button
          type="button"
          onClick={() => orgSlug && navigate(`/o/${orgSlug}/incidents`)}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-4)',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-1)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 500,
            }}
          >
            Open Incidents
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 'var(--space-1)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                color: openIncidentsCount > 0 ? 'var(--status-amber)' : 'var(--text)',
              }}
            >
              {openIncidentsCount}
            </span>
            <DeltaChip
              value={incidentsDelta}
              sentiment="negative"
              label="vs last week"
            />
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              marginTop: 'var(--space-1)',
            }}
          >
            Across {d.workspace_count} workspace{d.workspace_count === 1 ? '' : 's'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              style={{
                fontSize: 'var(--font-size-3xs)',
                color: 'var(--text-muted)',
              }}
            >
              View all
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
              style={{ color: 'var(--text-muted)' }}
            >
              <path
                d="M4.5 2.5l3 3.5-3 3.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>
      </div>

      {/* Per-workspace readiness breakdown */}
      {d.workspaces.length > 0 ? (
        <section
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-4)',
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            Workspace Readiness
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {d.workspaces.map((ws) => (
              <Link
                key={ws.workspace_id}
                to={`/w/${ws.slug}/app/dashboard`}
                style={{
                  textDecoration: 'none',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px, 200px) 1fr auto',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) 0',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span
                  style={{
                    fontWeight: 500,
                    color: 'var(--text)',
                    fontSize: 'var(--font-size-2xs)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ws.name}
                </span>
                <ScoreBar score={ws.score} />
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-2xs)',
                    color: scoreColor(ws.score),
                    minWidth: 40,
                    textAlign: 'right',
                  }}
                >
                  {ws.score}%
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* Top blockers */}
      {d.top_blockers.length > 0 ? (
        <section style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
            Top blockers
          </div>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
            {d.top_blockers.map((b, i) => (
              <li
                key={`${b.workspace_slug}-${b.ref}-${i}`}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)',
                  padding: 'var(--space-3) var(--space-4)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                  <SeverityBadge severity={mapSeverity(b.severity)} />
                  <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: 'var(--font-size-2xs)' }}>
                    {b.title}
                  </span>
                  <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                    {b.ref}
                  </span>
                </div>
                <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                  {b.workspace_name}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {/* Control health */}
      <section style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
          Control health
        </div>
        {controlHealth.loading ? (
          <Spinner label="Loading control health" />
        ) : controlHealth.error ? (
          <Alert variant="error">{controlHealth.error}</Alert>
        ) : controlHealth.data ? (
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 'var(--space-3)',
              }}
            >
              {(['passing', 'failing', 'error', 'suppressed'] as const).map((key) => (
                <div
                  key={key}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-card)',
                    background: 'var(--surface)',
                    padding: 'var(--space-3)',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {key}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
                    {controlHealth.data!.totals[key]}
                  </div>
                </div>
              ))}
            </div>
            {controlHealth.data.workspaces.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--font-size-2xs)',
                    color: 'var(--text)',
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: 'var(--space-2)', color: 'var(--text-muted)', fontWeight: 500 }}>Workspace</th>
                      <th style={{ textAlign: 'right', padding: 'var(--space-2)', color: 'var(--text-muted)', fontWeight: 500 }}>Total</th>
                      <th style={{ textAlign: 'right', padding: 'var(--space-2)', color: 'var(--text-muted)', fontWeight: 500 }}>Passing</th>
                      <th style={{ textAlign: 'right', padding: 'var(--space-2)', color: 'var(--text-muted)', fontWeight: 500 }}>Failing</th>
                      <th style={{ textAlign: 'right', padding: 'var(--space-2)', color: 'var(--text-muted)', fontWeight: 500 }}>Spec coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controlHealth.data.workspaces.map((ws) => (
                      <tr key={ws.workspace_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 'var(--space-2)' }}>{ws.name}</td>
                        <td style={{ padding: 'var(--space-2)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{ws.total}</td>
                        <td style={{ padding: 'var(--space-2)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{ws.passing}</td>
                        <td style={{ padding: 'var(--space-2)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{ws.failing}</td>
                        <td style={{ padding: 'var(--space-2)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {ws.total > 0 ? `${ws.passing}/${ws.total}` : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* Jurisdictions */}
      <section style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
          Jurisdictions
        </div>
        {jurisdictions.loading ? (
          <Spinner label="Loading jurisdictions" />
        ) : jurisdictions.error ? (
          <Alert variant="error">{jurisdictions.error}</Alert>
        ) : jurisdictions.data && jurisdictions.data.jurisdictions.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--space-3)',
            }}
          >
            {jurisdictions.data.jurisdictions.map((j) => (
              <div
                key={j.jurisdiction}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)',
                  padding: 'var(--space-3) var(--space-4)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{j.jurisdiction}</span>
                <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {j.legislation_count} item{j.legislation_count === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No jurisdictions"
            description="Jurisdiction coverage will appear once workspace registers include legislation items."
          />
        )}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

/** Simple as-of timestamp — lighter than FreshnessStamp (no reviewer context for org aggregates). */
function AsOfStamp() {
  const now = new Date();
  const formatted = now.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return (
    <div
      role="status"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize: 'var(--font-size-2xs)',
        color: 'var(--text-dim)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '9999px',
          background: 'var(--accent)',
        }}
      />
      As of {formatted}
    </div>
  );
}

/** Score color per brand-guide §6.2 rule 2 — same thresholds as ScoreBar. */
function scoreColor(score: number): string {
  if (score >= 85) return 'var(--success)';
  if (score >= 60) return 'var(--warning)';
  return 'var(--critical)';
}

/** Map a free-form severity string to the SeverityBadge's expected union. */
function mapSeverity(s: string): 'critical' | 'high' | 'elevated' | 'moderate' | 'standard' {
  const map: Record<string, 'critical' | 'high' | 'elevated' | 'moderate' | 'standard'> = {
    critical: 'critical',
    high: 'high',
    elevated: 'elevated',
    moderate: 'moderate',
    standard: 'standard',
    info: 'standard',
    low: 'standard',
  };
  return map[s] ?? 'standard';
}
